import { forwardRef, useImperativeHandle, useState } from "react";
import { createRoot } from "react-dom/client";
import { Button } from "../../../../../../../core/components/atoms/Button";
import Checkbox from "../../../../../../../core/components/atoms/Checkbox";
import { Typography } from "../../../../../../../core/components/molecules/Typography";
import { Modal } from "../../../../../../../core/components/templates/Modal";

/** localStorage key: when 'true', drag-and-drop completes without showing the confirmation modal */
export const SCHEDULE_ITEM_CHANGE_DONT_SHOW_KEY = "scheduleItemChangeModalDontShowAgain";

/** Use this before showing the modal: if false, user previously chose "don't show again" → complete action without modal */
export function shouldShowScheduleItemChangeModal(): boolean {
  return localStorage.getItem(SCHEDULE_ITEM_CHANGE_DONT_SHOW_KEY) !== "true";
}

export interface IScheduleItemChangeModalHandle {
  open: () => void;
  close: () => void;
}

interface IScheduleItemChangeModal {
  /** Controlled: when provided, isOpen/onClose control visibility */
  isOpen?: boolean;
  onClose?: () => void;
  /** Called when user clicks Confirm (in addition to onClose) */
  onConfirm?: () => void;
}

const ScheduleItemChangeModal = forwardRef<IScheduleItemChangeModalHandle, IScheduleItemChangeModal>(
  function ScheduleItemChangeModal(props, ref) {
    const { isOpen: controlledIsOpen, onClose: onCloseProp, onConfirm: onConfirmProp } = props;
    const [internalIsOpen, setInternalIsOpen] = useState(false);
    const [dontShowAgain, setDontShowAgain] = useState(false);

    const isControlled = controlledIsOpen !== undefined;
    const isOpen = isControlled ? controlledIsOpen : internalIsOpen;

    const close = () => {
      if (!isControlled) setInternalIsOpen(false);
      onCloseProp?.();
    };

    const open = () => {
      if (!isControlled) setInternalIsOpen(true);
    };

    useImperativeHandle(ref, () => ({
      open,
      close,
    }));

    const handleConfirm = () => {
      if (dontShowAgain) {
        localStorage.setItem(SCHEDULE_ITEM_CHANGE_DONT_SHOW_KEY, "true");
      }
      if (!isControlled) setInternalIsOpen(false);
      onConfirmProp?.();
      if (!onConfirmProp) onCloseProp?.();
    };

    return (
      <Modal isOpen={isOpen} onClose={close} size="md" title="Carga horária excedida" noPadding>
        <div className="flex flex-col">
          <div className="py-5 px-4">
            <Typography weight="bold" className="mb-4">
              Ao mover este conteúdo para este dia, a carga horária planejada será ultrapassada. Deseja continuar mesmo assim?
            </Typography>
            <Checkbox
              size="small"
              label="Não mostrar novamente"
              checked={dontShowAgain}
              onChange={() => setDontShowAgain((prev) => !prev)}
            />
          </div>
          <div className="flex items-center justify-center gap-sm border-t-hairline border-t-mono-color-light p-4">
            <Button variant="text" full onClick={close}>
              Cancelar
            </Button>
            <Button variant="contained" full onClick={handleConfirm}>
              Confirmar
            </Button>
          </div>
        </div>
      </Modal>
    );
  }
);

/**
 * Show the schedule item change modal from anywhere (e.g. another page).
 * Returns a Promise that resolves with true if the user confirmed, false if they cancelled or closed.
 *
 * @example
 * import { showScheduleItemChangeModal } from '.../ItemChangeModal';
 *
 * const handleMove = async () => {
 *   const confirmed = await showScheduleItemChangeModal();
 *   if (confirmed) {
 *     // proceed with move
 *   }
 * };
 */
export function showScheduleItemChangeModal(): Promise<boolean> {
  return new Promise((resolve) => {
    const container = document.createElement("div");
    container.setAttribute("data-schedule-item-change-modal", "true");
    document.body.appendChild(container);

    const root = createRoot(container);
    const ANIMATION_MS = 300;

    const finish = (value: boolean) => {
      resolve(value);
      setTimeout(() => {
        root.unmount();
        container.remove();
      }, ANIMATION_MS);
    };

    const ImperativeWrapper = () => {
      const [isOpen, setIsOpen] = useState(true);
      const handleClose = () => {
        setIsOpen(false);
        setTimeout(() => finish(false), ANIMATION_MS);
      };
      const handleConfirm = () => {
        setIsOpen(false);
        setTimeout(() => finish(true), ANIMATION_MS);
      };
      return (
        <ScheduleItemChangeModal
          isOpen={isOpen}
          onClose={handleClose}
          onConfirm={handleConfirm}
        />
      );
    };

    root.render(<ImperativeWrapper />);
  });
}

export default ScheduleItemChangeModal;